import { useState, useEffect, useMemo } from "react";

interface UseDispatchDelayCountdownResult {
  remainingSeconds: number;
  formattedRemainingTime: string;
}

interface ShipmentWithDelay {
  createdAt?: string | null; // a string integer (epoch seconds) from GraphQL, can be null/undefined
  dispatchDelaySecs?: number | null; // optional, can be null/undefined
}

/**
 * Formats remaining seconds into a human-readable string.
 * Examples: "45 seconds", "2 minutes 30 seconds", "1 hour 5 minutes"
 */
function formatRemainingTime(seconds: number): string {
  if (seconds <= 0) return "0 seconds";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
  }
  if (secs > 0 || parts.length === 0) {
    parts.push(`${secs} ${secs === 1 ? "second" : "seconds"}`);
  }

  return parts.join(" ");
}

/**
 * Calculates remaining dispatch delay seconds for a shipment.
 * Returns 0 if not blocked.
 */
function calculateRemainingDispatchDelay(
  createdAt: number | undefined | null,
  dispatchDelaySecs: number | undefined | null
): number {
  if (!createdAt || !dispatchDelaySecs || dispatchDelaySecs <= 0) {
    return 0;
  }
  const unlockTime = createdAt + dispatchDelaySecs;
  const now = Math.floor(Date.now() / 1000);
  return Math.max(0, unlockTime - now);
}

/**
 * Hook to manage dispatch delay countdown.
 * Returns whether dispatch is blocked and the remaining seconds until unblocked.
 *
 * @param shipments - Array of shipments with creation timestamp and dispatch delay seconds
 */
export function useDispatchDelayCountdown(
  shipments: ShipmentWithDelay[] | undefined | null
): UseDispatchDelayCountdownResult {
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  useEffect(() => {
    if (!shipments) return;

    let maxRemainingDelay = 0;
    let maxCreatedAt = 0;
    let maxDispatchDelaySecs = 0;
    for (const shipment of shipments) {
      const remaining = calculateRemainingDispatchDelay(
        Number(shipment.createdAt),
        shipment.dispatchDelaySecs
      );
      if (remaining > maxRemainingDelay) {
        maxRemainingDelay = remaining;
        maxCreatedAt = Number(shipment.createdAt);
        maxDispatchDelaySecs = shipment.dispatchDelaySecs ?? 0;
      }
    }
    setRemainingSeconds(maxRemainingDelay);

    if (maxRemainingDelay <= 0) return;

    const interval = setInterval(() => {
      const remaining = calculateRemainingDispatchDelay(
        maxCreatedAt,
        maxDispatchDelaySecs
      );
      if (remaining <= 0) clearInterval(interval);
      setRemainingSeconds(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [shipments]);

  return {
    remainingSeconds,
    formattedRemainingTime: formatRemainingTime(remainingSeconds),
  };
}
